import {
  forwardRef,
  useId,
  useState,
  type CSSProperties,
  type FocusEvent,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { AlertCircle } from 'lucide-react';
import { cn } from '../../utils/cn';
import {
  GLASS_INPUT_BG,
  GLASS_INPUT_BG_FOCUS,
  GLASS_INPUT_BORDER,
  GLASS_INPUT_BORDER_FOCUS,
  GLASS_FOCUS_RING,
  ON_GLASS_STRONG,
} from '../../styles/glassTokens';

const inputStyles = cva(
  [
    'block w-full rounded-lg border leading-none',
    'transition-colors duration-fast outline-none',
    'disabled:cursor-not-allowed',
  ].join(' '),
  {
    variants: {
      tone: {
        // Default light surface (warm-white canvas, the app standard).
        default: [
          'bg-white text-text-strong placeholder:text-text-subtle',
          'focus:border-brand-700 focus:ring-1 focus:ring-brand-300',
          'focus-visible:border-brand-700 focus-visible:ring-1 focus-visible:ring-brand-300',
          'disabled:bg-surface-muted disabled:text-text-muted',
          'read-only:bg-surface-muted',
        ].join(' '),
        // On the immersive dark-glass canvas. Colors come from glass tokens via
        // inline style (runtime constants, not Tailwind classes); this class set
        // only carries the structural bits so the two tones share a shape.
        'on-dark': 'disabled:opacity-60',
      },
      size: {
        sm: 'h-7 text-body-sm px-2',
        md: 'h-9 text-body-sm px-3',
        lg: 'h-11 text-body px-3.5',
      },
      invalid: {
        true: 'border-danger focus:ring-danger focus-visible:ring-danger focus:border-danger focus-visible:border-danger',
        false: '',
      },
    },
    compoundVariants: [
      // The default tone's resting border tokens (the on-dark tone supplies its
      // border via inline style, so it must not also get a Tailwind border color).
      { tone: 'default', invalid: false, class: 'border-border' },
    ],
    defaultVariants: { tone: 'default', size: 'md', invalid: false },
  },
);

type NativeProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>;

export interface InputProps extends NativeProps, VariantProps<typeof inputStyles> {
  iconLeading?: ReactNode;
  iconTrailing?: ReactNode;
  /** Visible label rendered above the input. Auto-wires `htmlFor`. */
  label?: ReactNode;
  /** Help text rendered below the input. Wired via `aria-describedby`. */
  description?: ReactNode;
  /** Error message rendered below the input. Sets `aria-invalid`,
   * is announced via `role="alert"`, and renders a danger icon inside
   * the field. Overrides `invalid` to true. */
  error?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, tone = 'default', size, invalid, iconLeading, iconTrailing, label, description, error, id, onFocus, onBlur, ...rest },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const descriptionId = description ? `${inputId}-description` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const isInvalid = invalid || Boolean(error);
  const describedBy = [descriptionId, errorId].filter(Boolean).join(' ') || undefined;
  const onDark = tone === 'on-dark';

  // The on-dark tone draws its glass surface + accent focus glow from tokens via
  // inline style. Focus is tracked in React (rather than Tailwind ring classes)
  // so the glow is custom to the glass; the structural Tailwind classes still
  // handle layout, radius, and disabled.
  const [focused, setFocused] = useState(false);
  const onDarkStyle: CSSProperties | undefined = onDark
    ? {
        background: focused ? GLASS_INPUT_BG_FOCUS : GLASS_INPUT_BG,
        border: `1px solid ${isInvalid ? '#FCA5A5' : focused ? GLASS_INPUT_BORDER_FOCUS : GLASS_INPUT_BORDER}`,
        color: ON_GLASS_STRONG,
        boxShadow: focused ? `0 0 0 3px ${GLASS_FOCUS_RING}` : 'none',
      }
    : undefined;

  // When invalid and no trailing icon supplied, surface a danger icon so the
  // error state isn't conveyed by border color alone (SC 1.4.1). On dark glass
  // the danger ramp is the lighter #FCA5A5 so the icon stays legible.
  const dangerIconClass = onDark ? 'size-4 text-[#FCA5A5]' : 'size-4 text-danger';
  const effectiveTrailing =
    iconTrailing ?? (isInvalid ? <AlertCircle className={dangerIconClass} /> : null);
  const adornmentColor = onDark ? 'text-white/70' : 'text-text-muted';

  const sharedInputProps = {
    ref,
    id: inputId,
    'aria-invalid': isInvalid || undefined,
    'aria-describedby': describedBy,
    style: onDarkStyle,
    className: cn(
      inputStyles({ tone, size, invalid: isInvalid }),
      onDark && 'glass-input',
      className,
    ),
    onFocus: onDark
      ? (e: FocusEvent<HTMLInputElement>) => {
          setFocused(true);
          onFocus?.(e);
        }
      : onFocus,
    onBlur: onDark
      ? (e: FocusEvent<HTMLInputElement>) => {
          setFocused(false);
          onBlur?.(e);
        }
      : onBlur,
    ...rest,
  };

  const inputEl =
    iconLeading || effectiveTrailing ? (
      <span className="relative inline-flex w-full items-center">
        {iconLeading ? (
          <span className={cn('pointer-events-none absolute left-3', adornmentColor)} aria-hidden>
            {iconLeading}
          </span>
        ) : null}
        <input
          {...sharedInputProps}
          className={cn(
            sharedInputProps.className,
            iconLeading && 'pl-9',
            effectiveTrailing && 'pr-9',
          )}
        />
        {effectiveTrailing ? (
          <span className={cn('pointer-events-none absolute right-3', adornmentColor)} aria-hidden>
            {effectiveTrailing}
          </span>
        ) : null}
      </span>
    ) : (
      <input {...sharedInputProps} />
    );

  if (!label && !description && !error) return inputEl;

  // The bare-Input path below renders its own label/description/error. On dark
  // glass these are usually composed by FormField/the page instead, but keep the
  // tone-aware colors here so a standalone on-dark Input with a label still reads.
  const labelClass = onDark ? 'text-body-sm font-medium text-white/90' : 'text-body-sm font-medium text-text-strong';
  const descClass = onDark ? 'text-body-sm text-white/70' : 'text-body-sm text-text-muted';
  const errClass = onDark ? 'flex items-center gap-1 text-body-sm text-[#FCA5A5]' : 'flex items-center gap-1 text-body-sm text-danger';

  return (
    <div className="flex flex-col gap-1">
      {label ? (
        <label htmlFor={inputId} className={labelClass}>
          {label}
        </label>
      ) : null}
      {inputEl}
      {description ? (
        <p id={descriptionId} className={descClass}>
          {description}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} role="alert" className={errClass}>
          <AlertCircle className="size-4" aria-hidden />
          {error}
        </p>
      ) : null}
    </div>
  );
});
